import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // GAVN custom (31/10 2026): åbn kurven MED DET SAMME og vis en "Lægger i kurv…"-linje øverst,
    // så kunden ikke venter et sekund uden at se noget. Når Shopify svarer, morph'es kurv-indholdet
    // og linjen erstattes af den rigtige kurvlinje (temaets egen "adding"-animation).
    // Quick-add fra en modal beholder den gamle adfærd: åbn først når varen er lagt i.
    const openNow = shouldAutoOpen && !sourceModal;
    /** @type {HTMLElement | null} */
    let pending = null;
    /** @type {((e: Event) => void) | null} */
    let onCartError = null;
    let fallbackTimer = 0;
    let closedForError = false;
    // Fejl (fx udsolgt): vis fejlen kort i linjen og luk så kurven — fejlbeskeden står også ved
    // købsknappen. Temaet gen-renderer kurven ved fejl, hvilket fjerner linjen efter et øjeblik.
    const closeForError = () => {
      if (closedForError) return;
      closedForError = true;
      pending?.remove();
      this.#themeDrawer?.close();
    };

    if (openNow) {
      pending = this.#showPending(event);
      this.#themeDrawer?.open();
      onCartError = (e) => {
        const message = /** @type {any} */ (e).error;
        this.#setPendingError(pending, typeof message === 'string' ? message : '');
        window.setTimeout(closeForError, 1000);
      };
      document.addEventListener('shopify:cart:error', onCartError, { once: true });
      // Sikkerhedsnet: fjern linjen hvis svaret aldrig kommer
      fallbackTimer = window.setTimeout(() => pending?.remove(), 15000);
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (openNow) {
          window.clearTimeout(fallbackTimer);
          if (onCartError) document.removeEventListener('shopify:cart:error', onCartError);

          if (detail?.didError) {
            this.#setPendingError(pending, '');
            window.setTimeout(() => {
              closeForError();
              settle();
            }, 1000);
            return;
          }

          // Morph af kurv-indholdet fjerner normalt linjen; ryd op hvis den overlevede
          window.setTimeout(() => {
            if (pending?.isConnected && pending.id === 'gavn-cart-pending') pending.remove();
            settle();
          }, 400);
          settle();
          return;
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        // GAVN custom: vent én frame, så kurv-indholdet er morph'et før skuffen åbner
        const openAndSettle = () =>
          requestAnimationFrame(() => {
            if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
            settle();
          });

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (openNow) {
          window.clearTimeout(fallbackTimer);
          if (onCartError) document.removeEventListener('shopify:cart:error', onCartError);
          // Afvist (fx "Komplet outfit" der fejler): fejlen vises på siden, så luk kurven igen
          closeForError();
        }
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  // ---------------------------------------------------------------------------
  // GAVN custom helpers (31/10 2026) — "Lægger i kurv…"-linje. Genindsæt efter temaopdatering.
  // Styles ligger i snippets/gavn-custom-styles.liquid (.gavn-cart-pending).
  // ---------------------------------------------------------------------------

  /** @returns {boolean} */
  #isDanish() {
    return (document.documentElement.lang || 'da').toLowerCase().startsWith('da');
  }

  /**
   * Finder billede, titel og variant for varen der lægges i kurv.
   * Produktside: sticky-købsbjælken har altid den valgte variant. Produktkort: kortets egne data.
   * @param {Event} event
   * @returns {{ image: string, alt: string, title: string, variant: string }}
   */
  #pendingProductInfo(event) {
    const text = (/** @type {Element | null | undefined} */ el) => el?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const empty = { image: '', alt: '', title: '', variant: '' };
    const lines = /** @type {any} */ (event).lines;
    // Flere varer på én gang (fx "Komplet outfit"): ingen enkelt-produktdata
    if (Array.isArray(lines) && lines.length > 1) return empty;

    const source = event.target instanceof Element ? event.target : null;
    if (!source) return empty;
    const card = source.closest('product-card');

    if (card) {
      const img = /** @type {HTMLImageElement | null} */ (card.querySelector('img'));
      return {
        image: img?.currentSrc || img?.src || '',
        alt: img?.alt || '',
        title: text(card.querySelector('[class*="title"]')) || img?.alt || '',
        variant: '',
      };
    }

    // Produktsiden: kun når kilden er sidens egen købsformular (sticky-bjælken klikker den)
    const form = /** @type {HTMLElement | null} */ (source.closest('product-form-component'));
    const sticky = /** @type {HTMLElement | null} */ (document.querySelector('sticky-add-to-cart'));
    if (!form || !sticky || form.dataset.productId !== sticky.dataset.productId) return empty;

    const stickyImg = /** @type {HTMLImageElement | null} */ (sticky?.querySelector('.sticky-add-to-cart__image-img'));
    const ogImage = document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '';
    const picker = /** @type {HTMLElement | null} */ (document.querySelector('variant-picker[data-product-title]'));

    let variant = text(sticky?.querySelector('.sticky-add-to-cart__variant'));
    const defaultTitle = sticky?.dataset.defaultVariantTitle || '';
    if (variant === defaultTitle || variant === 'Default Title') variant = '';

    return {
      image: stickyImg?.currentSrc || stickyImg?.src || ogImage,
      alt: stickyImg?.alt || '',
      title: text(sticky?.querySelector('.sticky-add-to-cart__title')) || picker?.dataset.productTitle || text(document.querySelector('h1')),
      variant,
    };
  }

  /**
   * Indsætter "Lægger i kurv…"-linjen øverst i kurven.
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #showPending(event) {
    const items = this.querySelector('.cart-drawer__items');
    if (!items) return null;

    items.querySelector('.gavn-cart-pending')?.remove();

    const info = this.#pendingProductInfo(event);
    // Eget tag + id: temaets morph må ALDRIG genbruge linjen som et andet element
    // (en <div> blev genbrugt som kurvens vare-wrapper og fjernet sammen med varerne).
    const row = document.createElement('gavn-cart-pending');
    row.id = 'gavn-cart-pending';
    row.className = 'gavn-cart-pending';
    row.setAttribute('role', 'status');
    row.setAttribute('aria-live', 'polite');

    if (info.image) {
      const media = document.createElement('div');
      media.className = 'gavn-cart-pending__media';
      const img = document.createElement('img');
      img.src = info.image;
      img.alt = info.alt;
      img.width = 120;
      img.height = 160;
      media.append(img);
      row.append(media);
    } else {
      row.classList.add('gavn-cart-pending--no-media');
    }

    const body = document.createElement('div');
    body.className = 'gavn-cart-pending__body';

    if (info.title) {
      const title = document.createElement('p');
      title.className = 'gavn-cart-pending__title';
      title.textContent = info.title;
      body.append(title);
    }
    if (info.variant) {
      const variant = document.createElement('p');
      variant.className = 'gavn-cart-pending__variant';
      variant.textContent = info.variant;
      body.append(variant);
    }

    const status = document.createElement('p');
    status.className = 'gavn-cart-pending__status';
    const spinner = document.createElement('span');
    spinner.className = 'gavn-cart-pending__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    const label = document.createElement('span');
    label.className = 'gavn-cart-pending__label';
    const lines = /** @type {any} */ (event).lines;
    const count = Array.isArray(lines) ? lines.reduce((n, l) => n + (Number(l?.quantity) || 0), 0) : 0;
    if (Array.isArray(lines) && lines.length > 1 && count > 1) {
      label.textContent = this.#isDanish() ? `Lægger ${count} varer i kurv…` : `Adding ${count} items to cart…`;
    } else {
      label.textContent = this.#isDanish() ? 'Lægger i kurv…' : 'Adding to cart…';
    }
    status.append(spinner, label);
    body.append(status);

    const bar = document.createElement('span');
    bar.className = 'gavn-cart-pending__bar';
    bar.setAttribute('aria-hidden', 'true');

    row.append(body, bar);
    items.prepend(row);
    return row;
  }

  /**
   * Skifter linjen til fejl-tilstand.
   * @param {HTMLElement | null} row
   * @param {string} message
   */
  #setPendingError(row, message) {
    if (!row || !row.isConnected) return;
    row.classList.add('gavn-cart-pending--error');
    const label = row.querySelector('.gavn-cart-pending__label');
    if (!label) return;
    if (message) {
      label.textContent = message;
    } else if (!row.dataset.gavnErrorShown) {
      label.textContent = this.#isDanish() ? 'Kunne ikke lægges i kurv' : 'Could not be added to cart';
    }
    row.dataset.gavnErrorShown = 'true';
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
